import React, { useEffect, useState } from 'react';
import { CalendarClock, Clock, CalendarDays } from 'lucide-react';
import { winterEnrollment } from '../../data/mock';
import useSiteMode from '../../hooks/useSiteMode';
import { nextMonthlyRelease, fmtDate } from '../../lib/winter';

const pad = (n) => String(n).padStart(2, '0');

function computeRemaining(target) {
  const diff = Math.max(0, target - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

function useCountdown(target) {
  const [t, setT] = useState(() => computeRemaining(target));
  useEffect(() => {
    const id = setInterval(() => setT(computeRemaining(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
}

export default function SessionCountdown({ variant = 'full' }) {
  const target = new Date(winterEnrollment.publicOpens).getTime();
  const t = useCountdown(target);
  const open = useSiteMode() === 'open';
  const next = nextMonthlyRelease();
  const monthlyLine = next ? `Next release: ${fmtDate(next.release)} for ${next.forMonth}` : '';

  if (variant === 'compact') {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6" data-testid="session-countdown-compact">
        <div className="flex items-center gap-3 rounded-2xl bg-splash-green text-splash-cream px-4 md:px-6 py-3 shadow-sm">
          <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-splash-coral/20">
            <Clock className="w-4 h-4 text-splash-coral" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-splash-cream/70 font-semibold">Winter 2026–27 Enrollment</div>
            <div className="font-semibold text-sm md:text-base">
              {open ? 'Now open — monthly memberships' : (
                <>Opens Fri, Oct 30 &middot; {' '}
                  <span className="tabular-nums text-white" data-testid="compact-countdown-values">{t.days}d {pad(t.hours)}h {pad(t.minutes)}m</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const units = [
    { v: t.days, label: 'Days' },
    { v: t.hours, label: 'Hours' },
    { v: t.minutes, label: 'Minutes' },
    { v: t.seconds, label: 'Seconds' },
  ];

  return (
    <section className="py-10 md:py-14" data-testid="session-countdown">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="rounded-3xl bg-splash-green text-splash-cream p-6 md:p-8 lg:p-10 shadow-xl">
          {/* Mobile: heading → countdown → details. Desktop: text left, countdown right. */}
          <div className="grid gap-5 lg:gap-x-8 lg:gap-y-2 lg:grid-cols-[1.1fr_auto]">
            <div className="min-w-0 lg:col-start-1 lg:row-start-1">
              <div className="inline-flex items-center gap-2 uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">
                <CalendarClock className="w-3.5 h-3.5" /> Winter 2026–27 Enrollment
              </div>
              <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight">
                {open ? 'Winter enrollment is open' : 'Enrollment opens in'}
              </h2>
            </div>
            {!open && (
              <div className="grid grid-cols-4 gap-2 md:gap-3 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" aria-live="polite" data-testid="countdown-values">
                {units.map((u) => (
                  <div key={u.label} data-testid={`countdown-${u.label.toLowerCase()}`} className="min-w-[64px] md:min-w-[80px] rounded-2xl bg-white/10 border border-white/15 px-2 py-3 md:py-4 text-center">
                    <div className="font-display font-bold text-2xl md:text-4xl tabular-nums text-white">{u.label === 'Days' ? u.v : pad(u.v)}</div>
                    <div className="mt-1 text-[10px] md:text-xs uppercase tracking-widest text-splash-cream/70 whitespace-nowrap">{u.label}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="min-w-0 lg:col-start-1 lg:row-start-2" data-testid="countdown-details">
              <p className="text-splash-cream/80 text-sm md:text-base">
                {open
                  ? 'Group lessons now run as a monthly membership — no more sessions to wait for.'
                  : 'Winter enrollment opens Friday, October 30, 2026.'}
              </p>
              {monthlyLine && (
                <p className="mt-3 inline-flex items-start gap-2 text-sm text-splash-cream/90" data-testid="countdown-monthly-release">
                  <CalendarDays className="w-4 h-4 mt-0.5 text-splash-coral shrink-0" />
                  <span><strong className="text-white">First Splash &amp; Little Splash Foundations™:</strong> {monthlyLine}.</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
